import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Store, UserRound, Lock, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { OctaPosMark } from '@/components/OctaPosMark'
import { ThemeToggle } from '@/components/ThemeToggle'
import { LanguageSwitch } from '@/components/LanguageSwitch'
import { t, tServer } from '@/i18n'
import { useSession } from '@/stores/session'
import { ApiError } from '@/lib/api'

/**
 * One login page for every tenant. The store code is what selects the database, so it is the
 * first field and is remembered per device — a cashier types it once, on the day the till is
 * set up, and never again.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const login = useSession((state) => state.login)

  const [storeCode, setStoreCode] = useState(() => localStorage.getItem('octapos.storeCode') ?? '')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      await login(storeCode, username, password)
      // Only remembered once it is known to be a real store, so a typo is not persisted.
      localStorage.setItem('octapos.storeCode', storeCode.trim().toUpperCase())
      navigate('/', { replace: true })
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? tServer(caught.detail ?? caught.message)
          : t('Could not reach the server. Check the connection and try again.'),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="safe-bottom flex min-h-dvh items-center justify-center px-4 py-10">
      {/* The language switch belongs here, before sign-in: somebody who cannot read English has
          to be able to change it without getting into the app first. */}
      <div className="fixed end-4 top-4 flex items-center gap-2">
        <LanguageSwitch />
        <ThemeToggle />
      </div>

      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <OctaPosMark title={null} className="h-14 w-14 text-[var(--accent)]" />
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">{t('OctaPOS')}</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            {t('Sign in to your store')}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="surface-raised flex flex-col gap-4 rounded-[var(--radius-card)] border p-6 shadow-sm"
        >
          <Input
            label={t('Store code')}
            value={storeCode}
            onChange={(event) => setStoreCode(event.target.value.toUpperCase())}
            placeholder="ACME"
            leading={<Store className="h-4 w-4" />}
            autoComplete="organization"
            autoCapitalize="characters"
            spellCheck={false}
            required
            className="tabular tracking-widest uppercase"
          />

          {/* A username, because this gets typed on a phone keyboard at the start of every
              shift. Shop staff hold no email address at all — see AppUser. */}
          {/* The placeholder is not translated: LoginName allows only a-z, digits and a few
              marks, so an Arabic example would be a username nobody could actually have. */}
          <Input
            label={t('Username')}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="sara"
            leading={<UserRound className="h-4 w-4" />}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
          />

          <Input
            label={t('Password')}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            leading={<Lock className="h-4 w-4" />}
            autoComplete="current-password"
            required
          />

          {error && (
            <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {error}
            </p>
          )}

          <Button type="submit" size="lg" full disabled={isSubmitting}>
            {isSubmitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {isSubmitting ? t('Signing in…') : t('Sign in')}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-[var(--text-secondary)]">
          {t('Trouble signing in? Contact your store administrator.')}
        </p>
      </div>
    </div>
  )
}
